'use client'

import { useCallback, useEffect, useState } from 'react'

import type { PriceModelData } from './price-section-types'

const getPricingMatrixSignature = (model: PriceModelData) =>
  [...model.levels]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((level) => `${level.id}:${level.lowestPrice ?? ''}`)
    .join('|')

/**
 * The selected model × technology level for the pricing section, shared by the
 * seller-card control and the tier-ladder treatment so a deep link or a model
 * switch behaves identically in both arms.
 */
export const usePriceSelection = ({ models, defaultModelId }: { models: PriceModelData[]; defaultModelId: number }) => {
  const initialModel = models.find((model) => model.id === defaultModelId) ?? models[0]
  const [selectedModelId, setSelectedModelId] = useState(initialModel.id)
  const [selectedLevelId, setSelectedLevelId] = useState(initialModel.defaultLevelId)

  const selectedModel = models.find((model) => model.id === selectedModelId) ?? initialModel
  const selectedLevel = selectedModel.levels.find((level) => level.id === selectedLevelId)
  const initialPricingMatrix = getPricingMatrixSignature(initialModel)
  // Model choice is offered only when it changes the available tiers or their prices.
  const pricesVaryByModel =
    models.length > 1 && models.some((model) => getPricingMatrixSignature(model) !== initialPricingMatrix)

  /**
   * Switch model. With `keepLevel`, the current tier survives when the new
   * model offers it (the ladder's behavior); otherwise the model's default tier
   * is selected (the segmented control's behavior).
   */
  const selectModel = useCallback(
    (modelId: number, keepLevel = false) => {
      const model = models.find((candidate) => candidate.id === modelId)
      if (!model) return
      setSelectedModelId(model.id)
      setSelectedLevelId((current) =>
        keepLevel && model.levels.some((level) => level.id === current) && model.levelData[current]
          ? current
          : model.defaultLevelId
      )
    },
    [models]
  )

  const selectVariant = useCallback(
    (modelId: number, levelId: number) => {
      const model = models.find((candidate) => candidate.id === modelId)
      if (!model?.levels.some((level) => level.id === levelId) || !model.levelData[levelId]) {
        return
      }
      setSelectedModelId(model.id)
      setSelectedLevelId(levelId)
    },
    [models]
  )

  // Deep links (e.g. the brand hub's tech-level price ranges) preselect a tier
  // via `?level=<levels.name>` + `#prices`. Read on mount only — client-side,
  // so the cached page stays static; unknown or offer-less levels are ignored.
  // Matches the raw tier token first ("7"), the display label as a fallback.
  // If the current model does not offer it, select the first real model × tier
  // combination that does — a deep link must never manufacture a sparse cell.
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('level')?.toLowerCase()
    if (!param) return
    for (const model of [selectedModel, ...models.filter((candidate) => candidate.id !== selectedModel.id)]) {
      const match =
        model.levels.find((level) => level.levelName.toLowerCase() === param) ??
        model.levels.find((level) => level.name.toLowerCase() === param)
      if (match && model.levelData[match.id]) {
        setSelectedModelId(model.id)
        setSelectedLevelId(match.id)
        return
      }
    }
  }, [models, selectedModel])

  return {
    initialModel,
    selectedModel,
    selectedLevelId,
    selectedLevel,
    pricesVaryByModel,
    selectModel,
    selectLevel: setSelectedLevelId,
    selectVariant,
  }
}
